// A package file in an editor tab: line-numbered view, editable for text
// files; saving makes a new draft revision. catalogue.json of a protobuf
// type is generated by the engine (read-only here); binary samples are not
// shown as text.
import { useEffect, useRef, useState } from "preact/hooks";
import type { Actions } from "./model";
import { t } from "../i18n";

const GENERATED = new Set(["catalogue.json"]);

export function isBinary(path: string): boolean {
  return /\.(bin|png|jpg|jpeg|gif|webp|pdf)$/i.test(path);
}

export function FileView({ path, rev, actions, writer }: { path: string; rev: number; actions: Actions; writer: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const [orig, setOrig] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const gutter = useRef<HTMLDivElement>(null);
  const editable = writer && !GENERATED.has(path) && !isBinary(path);

  useEffect(() => {
    setText(null);
    setErr("");
    if (isBinary(path)) {
      setText("");
      return;
    }
    actions.readFile(path).then((t) => { setText(t); setOrig(t); }).catch((e) => setErr((e as Error).message));
  }, [path, rev]);

  if (err) return <p class="p-4 text-crit">{err}</p>;
  if (text === null) return <p class="p-4 text-muted">{t("wb.file.loading", { path })}</p>;
  if (isBinary(path)) return <p class="p-4 text-muted">{t("wb.file.binary", { path })}</p>;
  const lines = text.split("\n").length;
  const dirty = text !== orig;
  return (
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="flex h-10 flex-none items-center gap-3 border-b border-line px-4 text-[12.5px] text-muted">
        <span class="font-mono text-ink2">{path}</span>
        <span>r{rev}</span>
        {GENERATED.has(path) && <span class="pill bg-hover text-ink2">{t("wb.file.generated")}</span>}
        {!editable && !GENERATED.has(path) && <span class="pill bg-hover text-ink2">{t("wb.file.readonly")}</span>}
        <span class="flex-1" />
        {dirty && <span class="text-warn">{t("wb.file.unsaved")}</span>}
        {editable && dirty && <button type="button" class="btn h-7" onClick={() => setText(orig)}>{t("wb.file.discard")}</button>}
        {editable && (
          <button type="button" class="btn btn-pri h-7" disabled={!dirty || busy} onClick={async () => {
            setBusy(true);
            try {
              await actions.saveFile(path, text);
              setOrig(text);
            } catch (e) {
              setErr((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}>{busy ? t("wb.file.saving") : t("wb.file.save")}</button>
        )}
      </div>
      <div class="flex min-h-0 flex-1 overflow-hidden font-mono text-[12.5px] leading-[20px]">
        <div ref={gutter} aria-hidden="true" class="w-14 flex-none select-none overflow-hidden border-r border-line bg-side py-2 pr-3 text-right text-dim">
          {Array.from({ length: lines }, (_, i) => <div>{i + 1}</div>)}
        </div>
        <textarea aria-label={t("wb.file.content", { path })} spellcheck={false} readOnly={!editable} value={text} wrap="off"
          onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          onScroll={(e) => { if (gutter.current) gutter.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop; }}
          class="min-h-0 flex-1 resize-none bg-main px-3 py-2 leading-[20px] text-ink outline-none" />
      </div>
    </div>
  );
}
